import { memo, type ReactNode, useCallback } from 'react';

import { useTrackSmartSearchFeedback } from '@air/analytics';
import { ThumbDownIcon, ThumbUpIcon } from '@air/next-icons';
import { useToasts } from '@air/provider-toast';

import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

const TALLY_FEEDBACK_URL = 'https://tally.so/r/m6qo4e';
const TALLY_FEEDBACK_QUERY_PARAM = 'query';
const TALLY_FEEDBACK_WSID_PARAM = 'wsid';

const TOAST_MESSAGE = 'Thank you for your feedback!';

type FeedbackButtonProps = {
  children: ReactNode;
  onClick?: () => void;
};

const FeedbackButton = ({ children, onClick }: FeedbackButtonProps) => (
  <button
    className="flex flex-col items-center justify-center rounded border border-grey-5 bg-grey-1 p-1 transition-colors hover:bg-grey-3"
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

export const SmartSearchBottomBar = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { trackSmartSearchFeedback } = useTrackSmartSearchFeedback();
  const { showToast } = useToasts();
  const { data: subscription } = useSubscriptionContext();
  const { isSemanticSearchEnabled } = usePrivateSearchContext();
  const { search, hasSearchParams } = useSearchParams();

  const hasMetPrerequisites =
    isSemanticSearchEnabled && hasSearchParams && !!subscription && !!search && !!currentWorkspace;

  const onClickThumbsUp = useCallback(() => {
    if (hasMetPrerequisites) {
      trackSmartSearchFeedback({ sentiment: 'thumbs_up' });
      showToast(TOAST_MESSAGE);
    }
  }, [hasMetPrerequisites, trackSmartSearchFeedback, showToast]);

  const onClickThumbsDown = useCallback(() => {
    if (hasMetPrerequisites) {
      trackSmartSearchFeedback({ sentiment: 'thumbs_down' });
      showToast(TOAST_MESSAGE);

      // TODO: Follow-up to replace this redirect with Tally feedback widget
      // Ticket: https://linear.app/air/issue/AI-213/implement-tally-widget-for-smart-search-feedback
      const url = new URL(TALLY_FEEDBACK_URL);
      url.searchParams.set(TALLY_FEEDBACK_QUERY_PARAM, search);
      url.searchParams.set(TALLY_FEEDBACK_WSID_PARAM, currentWorkspace.id);

      window.open(url.toString(), '_blank');
    }
  }, [hasMetPrerequisites, trackSmartSearchFeedback, showToast, search, currentWorkspace]);

  return hasMetPrerequisites ? (
    <div className="sticky inset-x-0 bottom-0 z-30 flex h-12 w-full items-center justify-between bg-grey-1/85 px-4 py-3 backdrop-blur-[18px]">
      <div className="flex items-center gap-1">
        <span className="font-normal text-12 text-grey-11">
          Smart search uses AI to expand your search terms with related concepts for more relevant results. Results may
          vary.
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-normal text-12 text-grey-11">How are the results?</span>
        <div className="flex items-center gap-2">
          <FeedbackButton onClick={onClickThumbsUp}>
            <ThumbUpIcon className="size-4 text-grey-12" />
          </FeedbackButton>
          <FeedbackButton onClick={onClickThumbsDown}>
            <ThumbDownIcon className="size-4 text-grey-12" />
          </FeedbackButton>
        </div>
      </div>
    </div>
  ) : null;
});

SmartSearchBottomBar.displayName = 'SmartSearchBottomBar';
